import { getDashboardPreferences, saveDashboardPreferences } from '../api';
import { DASHBOARD_CARDS, getDefaultDashboardPreferences } from '../config/dashboardCardRegistry';

const STORAGE_KEY = 'crm_dashboard_prefs_v1';

/**
 * Read dashboard prefs from localStorage.
 */
function readLocalStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Write dashboard prefs to localStorage.
 */
function writeLocalStorage(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // silent fail
  }
}

/**
 * Merge saved preferences with current card registry.
 * Handles cards being added or removed between releases.
 */
function mergeWithDefaults(savedCards) {
  if (!savedCards || savedCards.length === 0) return getDefaultDashboardPreferences();

  const savedMap = new Map(savedCards.map(c => [c.id, c]));
  const registryIds = new Set(DASHBOARD_CARDS.map(c => c.id));
  const merged = [];

  // Keep saved cards that still exist in registry
  savedCards.forEach(saved => {
    if (registryIds.has(saved.id)) {
      merged.push({
        id: saved.id,
        visible: saved.visible,
        order: saved.order,
        size: saved.size,
      });
    }
  });

  // Append new cards not in saved prefs
  DASHBOARD_CARDS.forEach(card => {
    if (!savedMap.has(card.id)) {
      merged.push({
        id: card.id,
        visible: card.defaultVisible,
        order: merged.length,
        size: card.defaultSize,
      });
    }
  });

  return merged;
}

/**
 * Get dashboard preferences. Tries API first, falls back to localStorage.
 */
async function getPreferences() {
  // Try API
  try {
    const response = await getDashboardPreferences();
    if (response.data && response.data.cards && response.data.cards.length > 0) {
      const cards = mergeWithDefaults(response.data.cards);
      writeLocalStorage({ cards, lastUpdated: new Date().toISOString() });
      return cards;
    }
  } catch {
    // API failed
  }

  // Fallback: localStorage
  const local = readLocalStorage();
  if (local && local.cards && local.cards.length > 0) {
    return mergeWithDefaults(local.cards);
  }

  // No saved prefs
  return getDefaultDashboardPreferences();
}

/**
 * Save dashboard preferences. Saves to API + localStorage.
 */
async function savePreferences(cards) {
  const prefs = { cards, lastUpdated: new Date().toISOString() };
  writeLocalStorage(prefs);

  try {
    await saveDashboardPreferences(cards);
  } catch {
    // localStorage is fallback
  }
}

/**
 * Reset to defaults.
 */
async function resetToDefaults() {
  const defaults = getDefaultDashboardPreferences();
  await savePreferences(defaults);
  return defaults;
}

export { getPreferences, savePreferences, resetToDefaults, mergeWithDefaults };
